import React, { Suspense, lazy, useEffect } from 'react';
import 'regenerator-runtime/runtime';
import { LoadingScreen } from 'src/components/LoadingScreen';
import { useFinishExecution } from 'src/hooks/useFinishExecution';
import { useErrorContext } from 'src/contexts/ErrorContext';
import { useLogin } from 'src/hooks/useLogin';
import { CustomError } from '../CustomError';
import { TenantSelector } from '../TenantSelector';
import { Result } from 'src/types/result';
import { Logger } from 'src/utils/Logger';
import { ErrorCodes } from 'src/types/error';
import { OnboardingStep } from 'src/types/onboardingStep';
import { SIMPLEUI_EVENT_MAP } from 'src/constants/SimpleUIEvents';
import { TENANTLESS } from 'src/constants/commons';
import { useShellRootContext } from 'src/contexts/ShellRoot';
import { publishEvent } from 'src/analytics/publishEvent';
import { ScreenAction } from 'src/analytics/types';
import { ConditionalModalContainer } from '../ConditionalModalContainer';
import { ConditionalSetupAndOnboardingViewContainer } from '../ConditionalSetupContainer';

const OnboardingComponent = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding-component" */
      '../OnboardingComponent'
    )
);

const ValuePropositionComponent = lazy(() =>
  import(
    /* webpackChunkName: "value-proposition-component" */
    '../ValuePropositionComponent'
  ).then((module) => ({
    default: module.ValuePropositionComponent
  }))
);

const FullScreenUnrecoverableError = lazy(() =>
  import(
    /* webpackChunkName: "full-screen-unrecoverable-error" */
    '../FullScreenUnrecoverableError'
  ).then((module) => ({
    default: module.FullScreenUnrecoverableError
  }))
);

const UserOnboarding = () => {
  const { finishOnError } = useFinishExecution();
  const { error, authError, isCustomError } = useErrorContext();
  const { handleLogin, onboardingStep } = useLogin();
  const {
    interfaces: { v1 }
  } = useShellRootContext();

  const loadingScreenFlow = getLoadingScreenFlow(v1);

  const stepComponents = {
    [OnboardingStep.ONBOARD_USER]: OnboardingComponent,
    [OnboardingStep.SELECT_TENANT]: TenantSelector,
    [OnboardingStep.VALUE_PROPOSITION]: ValuePropositionComponent
  };

  Logger.log('OnboardingStep:', onboardingStep);

  useEffect(() => {
    Logger.log({ onboardingStep });

    if (!authError && onboardingStep === null) handleLogin();
  }, [authError, handleLogin, onboardingStep]);

  const renderContent = () => {
    if (error) {
      if (isCustomError && error.errorCode === ErrorCodes.LoginError) {
        Logger.log('User redirect to safety net screen');
        publishEvent({
          type: ScreenAction.REDIRECT_TO_SAFETY_NET_SCREEN_ERROR
        });
        return <CustomError />;
      }

      return (
        <Suspense fallback={<LoadingScreen flow={loadingScreenFlow} />}>
          <FullScreenUnrecoverableError
            show={Boolean(error)}
            actionLabel="Continue"
            actionCallback={() => {
              Logger.log('User acknowledge unrecoverable error');
              finishOnError(Result.Failure, error);
            }}
            errorCode={error?.errorCode}
          />
        </Suspense>
      );
    }

    const StepComponent = stepComponents[onboardingStep];
    if (StepComponent) {
      return (
        <Suspense fallback={<LoadingScreen flow={loadingScreenFlow} />}>
          <StepComponent />
        </Suspense>
      );
    }

    return <LoadingScreen flow={loadingScreenFlow} />;
  };

  return (
    <ConditionalModalContainer onboardingStep={onboardingStep}>
      <ConditionalSetupAndOnboardingViewContainer
        onboardingStep={onboardingStep}
      >
        {renderContent()}
      </ConditionalSetupAndOnboardingViewContainer>
    </ConditionalModalContainer>
  );
};

const getLoadingScreenFlow = (v1) => {
  const isLoggedIn = v1?.sessionInterface?.isLoggedIn();
  const isTenantless = v1?.authToken?.getCurrentContext() === TENANTLESS;

  return isLoggedIn && isTenantless
    ? SIMPLEUI_EVENT_MAP.LOADING_SPINNER.POST_SIGN_IN_OR_CREATE_ACCOUNT
    : SIMPLEUI_EVENT_MAP.LOADING_SPINNER.INITIAL;
};

export default UserOnboarding;
